import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { remove } from 'aws-amplify/storage';
import { useAuth } from '../../contexts/AuthContext';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { extractStorageKey } from '../../utils/storageImageUrl';
import StorageImage from '../StorageImage';
import { formatClubDate } from '../../utils/clubTime';

// Use Cognito user pool auth for admin operations (updates/deletes require authentication)
const client = generateClient({ authMode: 'userPool' });

interface Announcement {
  id: string;
  title: string;
  content: string;
  excerpt?: string;
  publishDate: string;
  expiryDate?: string;
  isActive: boolean;
  isPinned: boolean;
  category: 'GENERAL' | 'TIDE_REPORT' | 'FISHING_TIPS' | 'CLUB_NEWS' | 'URGENT';
  authorName?: string;
  imageUrl?: string;
  tags?: string[];
  viewCount?: number;
  createdAt: string;
  updatedAt: string;
}

type AnnouncementFormData = Omit<Announcement, 'id' | 'createdAt' | 'updatedAt' | 'viewCount'>;

const AnnouncementManagement: React.FC = () => {
  const { userId } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [extracting, setExtracting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const [formData, setFormData] = useState<AnnouncementFormData>({
    title: '',
    content: '',
    excerpt: '',
    publishDate: new Date().toISOString().split('T')[0],
    expiryDate: '',
    isActive: true,
    isPinned: false,
    category: 'GENERAL',
    authorName: '',
    imageUrl: '',
    tags: [],
  });

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await client.models.Announcement.list();

      if (result.errors && result.errors.length > 0) {
        console.error('GraphQL errors loading announcements:', result.errors);
        setError(`Failed to load announcements: ${result.errors[0].message}`);
        return;
      }

      if (result.data) {
        const sorted = [...result.data].sort(
          (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
        );
        setAnnouncements(sorted as Announcement[]);
      }
    } catch (err) {
      console.error('Error loading announcements:', err);
      setError('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setUploading(true);
      let imageUrl = formData.imageUrl;

      // Upload image if a new file was selected
      if (imageFile) {
        // Server-side validated presigned URL flow (admin-only, MIME + size enforced in Lambda)
        const result = await (client.queries as any).generateUploadUrl({
          purpose: 'ANNOUNCEMENT_IMAGE',
          fileName: imageFile.name,
          contentType: imageFile.type,
          identityId: userId || '',
        });
        const uploadData = parseLambdaResponse(result?.data);
        if (!uploadData?.uploadUrl || !uploadData?.s3Key) throw new Error('Failed to get upload URL');

        await fetch(uploadData.uploadUrl, {
          method: 'PUT',
          body: imageFile,
          headers: { 'Content-Type': imageFile.type },
        });

        // Store the plain S3 key, not a baked/presigned URL — see
        // storageImageUrl.ts for why a stripped presigned URL never
        // actually works for this guest-readable-but-IAM-gated path.
        imageUrl = uploadData.s3Key;
      }

      if (editingAnnouncement) {
        // Delete old image if a new one was uploaded
        if (imageFile && editingAnnouncement.imageUrl) {
          try {
            const oldKey = extractStorageKey(editingAnnouncement.imageUrl);
            if (oldKey) await remove({ path: oldKey });
          } catch (err) {
            console.error('Error deleting old image:', err);
          }
        }

        // Update existing announcement
        const updateResult = await client.models.Announcement.update({
          id: editingAnnouncement.id,
          ...formData,
          expiryDate: formData.expiryDate || undefined,
          imageUrl,
        });
        if (updateResult.errors && updateResult.errors.length > 0) {
          console.error('GraphQL errors updating announcement:', updateResult.errors);
          showToast(`Failed to update: ${updateResult.errors[0].message}`, 'error');
          return;
        }
        showToast('Announcement updated successfully', 'success');
      } else {
        // Create new announcement
        const createResult = await client.models.Announcement.create({
          ...formData,
          expiryDate: formData.expiryDate || undefined,
          imageUrl,
        });
        if (createResult.errors && createResult.errors.length > 0) {
          console.error('GraphQL errors creating announcement:', createResult.errors);
          showToast(`Failed to create: ${createResult.errors[0].message}`, 'error');
          return;
        }
        showToast('Announcement created successfully', 'success');
      }

      resetForm();
      await loadAnnouncements();
    } catch (err) {
      console.error('Error saving announcement:', err);
      showToast('Failed to save announcement', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      excerpt: announcement.excerpt || '',
      publishDate: announcement.publishDate,
      expiryDate: announcement.expiryDate || '',
      isActive: announcement.isActive,
      isPinned: announcement.isPinned,
      category: announcement.category,
      authorName: announcement.authorName || '',
      imageUrl: announcement.imageUrl || '',
      tags: announcement.tags || [],
    });
    setImagePreview('');
    setImageFile(null);
    setShowForm(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file', 'error');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        showToast('Image size must be less than 10MB', 'error');
        return;
      }

      // Magic-byte validation: read first 4 bytes to confirm actual image format
      const reader = new FileReader();
      reader.onloadend = () => {
        const arr = new Uint8Array(reader.result as ArrayBuffer).subarray(0, 4);
        const header = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
        // JPEG: ffd8ff, PNG: 89504e47, GIF: 47494638, WebP: 52494646
        const isValidImage =
          header.startsWith('ffd8ff') ||       // JPEG
          header.startsWith('89504e47') ||      // PNG
          header.startsWith('47494638') ||      // GIF
          header.startsWith('52494646');        // WebP (RIFF header)

        if (!isValidImage) {
          showToast('Invalid image file. Only JPEG, PNG, GIF, and WebP are allowed.', 'error');
          return;
        }

        setImageFile(file);
        const previewReader = new FileReader();
        previewReader.onloadend = () => setImagePreview(previewReader.result as string);
        previewReader.readAsDataURL(file);
      };
      reader.readAsArrayBuffer(file.slice(0, 4));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData({ ...formData, imageUrl: '' });
  };

  const handleSourceFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Please select a PDF, JPEG, PNG, or WebP file', 'error');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showToast('File size must be less than 25MB', 'error');
      return;
    }

    setExtracting(true);
    try {
      // Presigned-upload the source file (admin-only, MIME + size enforced server-side)
      const uploadResult = await (client.queries as any).generateUploadUrl({
        purpose: 'ANNOUNCEMENT_SOURCE',
        fileName: file.name,
        contentType: file.type,
        identityId: userId || '',
      });
      const uploadData = parseLambdaResponse(uploadResult?.data);
      if (!uploadData?.uploadUrl || !uploadData?.s3Key) throw new Error('Failed to get upload URL');

      await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      // Invoke extraction — synchronous, can take up to ~25s for a 5-page PDF
      const extractResult = await (client.mutations as any).extractAnnouncement({
        sourceS3Key: uploadData.s3Key,
      });
      const draft = parseLambdaResponse(extractResult?.data);

      if (!draft || draft.error) {
        showToast(draft?.error || 'Could not extract content from this file', 'error');
        return;
      }

      // Pre-fill the same form Method 1 uses — never saved automatically.
      setEditingAnnouncement(null);
      setImageFile(null);
      setImagePreview('');
      setFormData({
        title: draft.title || '',
        content: draft.content || '',
        excerpt: draft.excerpt || '',
        publishDate: new Date().toISOString().split('T')[0],
        expiryDate: '',
        isActive: true,
        isPinned: false,
        category: draft.category || 'GENERAL',
        authorName: '',
        imageUrl: draft.imageS3Key || '',
        tags: [],
      });
      setShowForm(true);
      showToast('Draft created from your file — review before publishing', 'success');
    } catch (err) {
      console.error('Error extracting announcement:', err);
      showToast('Could not process this file. You can still start a new announcement manually.', 'error');
    } finally {
      setExtracting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      const result = await client.models.Announcement.delete({ id });
      if (result.errors && result.errors.length > 0) {
        console.error('GraphQL errors deleting announcement:', result.errors);
        showToast(`Failed to delete: ${result.errors[0].message}`, 'error');
        return;
      }
      showToast('Announcement deleted', 'success');
      await loadAnnouncements();
    } catch (err) {
      console.error('Error deleting announcement:', err);
      showToast('Failed to delete announcement', 'error');
    }
  };

  const handleToggleActive = async (announcement: Announcement) => {
    try {
      const result = await client.models.Announcement.update({
        id: announcement.id,
        isActive: !announcement.isActive,
      });
      if (result.errors && result.errors.length > 0) {
        showToast(`Failed to update status: ${result.errors[0].message}`, 'error');
        return;
      }
      await loadAnnouncements();
    } catch (err) {
      console.error('Error toggling active status:', err);
      showToast('Failed to update status', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      publishDate: new Date().toISOString().split('T')[0],
      expiryDate: '',
      isActive: true,
      isPinned: false,
      category: 'GENERAL',
      authorName: '',
      imageUrl: '',
      tags: [],
    });
    setEditingAnnouncement(null);
    setShowForm(false);
    setImageFile(null);
    setImagePreview('');
  };

  const filteredAnnouncements = announcements.filter(announcement => {
    const matchesSearch = 
      announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      announcement.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'ALL' || announcement.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString: string) => {
    return formatClubDate(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getCategoryBadge = (category: string) => {
    const styles = {
      GENERAL: 'bg-gray-100 text-gray-800',
      TIDE_REPORT: 'bg-blue-100 text-blue-800',
      FISHING_TIPS: 'bg-green-100 text-green-800',
      CLUB_NEWS: 'bg-purple-100 text-purple-800',
      URGENT: 'bg-red-100 text-red-800',
    };
    return styles[category as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div role="alert" aria-live="assertive" className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Announcement Management</h2>
        <div className="flex items-center gap-3">
          <label className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 ${extracting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            {extracting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing…
              </>
            ) : (
              <>
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload PDF or Image
              </>
            )}
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={handleSourceFileUpload}
              disabled={extracting}
              className="hidden"
            />
          </label>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700"
          >
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Announcement
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search announcements..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            >
              <option value="ALL">All Categories</option>
              <option value="GENERAL">General</option>
              <option value="TIDE_REPORT">Tide Report</option>
              <option value="FISHING_TIPS">Fishing Tips</option>
              <option value="CLUB_NEWS">Club News</option>
              <option value="URGENT">Urgent</option>
            </select>
          </div>
        </div>
      </div>

      {/* Announcements List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fishing-blue"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadAnnouncements}
              className="text-fishing-blue hover:text-blue-700"
            >
              Try Again
            </button>
          </div>
        ) : filteredAnnouncements.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No announcements found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredAnnouncements.map((announcement) => (
              <div key={announcement.id} className="p-6 hover:bg-gray-50">
                <div className="flex justify-between items-start gap-4">
                  <StorageImage
                    storageKey={announcement.imageUrl}
                    alt={announcement.title}
                    className="w-24 h-24 object-cover rounded flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {announcement.title}
                      </h3>
                      {announcement.isPinned && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          📌 Pinned
                        </span>
                      )}
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryBadge(announcement.category)}`}>
                        {announcement.category.replace('_', ' ')}
                      </span>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        announcement.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {announcement.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {announcement.excerpt || announcement.content.substring(0, 150) + '...'}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Published: {formatDate(announcement.publishDate)}</span>
                      {announcement.expiryDate && (
                        <span>Expires: {formatDate(announcement.expiryDate)}</span>
                      )}
                      {announcement.authorName && (
                        <span>By: {announcement.authorName}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleToggleActive(announcement)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                      title={announcement.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleEdit(announcement)}
                      className="p-2 text-fishing-blue hover:text-blue-700"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(announcement.id)}
                      className="p-2 text-red-600 hover:text-red-700"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-medium text-gray-900">
                {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              <div>
                <label htmlFor="announcement-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  id="announcement-title"
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                />
              </div>

              <div>
                <label htmlFor="announcement-content" className="block text-sm font-medium text-gray-700 mb-1">
                  Content *
                </label>
                <textarea
                  id="announcement-content"
                  required
                  rows={6}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image (Optional)
                </label>
                <div className="mt-1 flex items-center gap-4">
                  <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                    <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Choose Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                  {(imagePreview || formData.imageUrl) && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Upload an image (max 10MB). Supported formats: JPG, PNG, GIF, WebP
                </p>
                {imagePreview ? (
                  <div className="mt-3">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="max-h-48 rounded border border-gray-300"
                    />
                  </div>
                ) : formData.imageUrl ? (
                  <div className="mt-3">
                    <StorageImage
                      storageKey={formData.imageUrl}
                      alt={editingAnnouncement ? 'Current announcement image' : 'Extracted image'}
                      className="max-h-48 rounded border border-gray-300"
                    />
                  </div>
                ) : null}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  >
                    <option value="GENERAL">General</option>
                    <option value="TIDE_REPORT">Tide Report</option>
                    <option value="FISHING_TIPS">Fishing Tips</option>
                    <option value="CLUB_NEWS">Club News</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Author Name
                  </label>
                  <input
                    type="text"
                    value={formData.authorName}
                    onChange={(e) => setFormData({ ...formData, authorName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Publish Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.publishDate}
                    onChange={(e) => setFormData({ ...formData, publishDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiry Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isPinned}
                    onChange={(e) => setFormData({ ...formData, isPinned: e.target.checked })}
                    className="h-4 w-4 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Pin to Top</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={uploading}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {uploading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    `${editingAnnouncement ? 'Update' : 'Create'} Announcement`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementManagement;
